import { Input } from "@/components/inputs/input";
import { Button } from "@/components/ui/button";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { CreateBookingDataParams } from "../../adapters/create-booking/create-booking.types";
import { SearchInputCommand } from "@/components/inputs/search-input-command";
import { useFilters } from "@/hooks/use-filters";
import { JustNumber } from "@/utils/format/regex/just-number";
import { JustDecimal } from "@/utils/format/regex/just-decimal";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreateBookingFormData,
  PackageDetails,
} from "./booking-create-page.types";
import { CreateBookingSchema } from "./booking-create-page.validation";
import { CalcCreditsToConsume } from "../../adapters/get-privateroom-price-by-company-package";
import { getSpaceTakeValue } from "@/utils/get-space-take-value/get-space-take-value";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { SirenIcon, Receipt } from "lucide-react";
import { isProRataDate } from "@/utils/is-pro-rata-date";
import {
  differenceInCalendarMonths,
  isBefore,
  parse,
  parseISO,
  startOfMonth,
} from "date-fns";
import { useGetPackages } from "../../../../../hooks/use-get-packages/use-get-packages";
import { GetCompanyGroupsByIdList } from "../../adapters/get-company-group-by-id/get-company-group-by-id-list";
import { ConvertProductToName } from "@/utils/convert-product-to-name";
import { ConvertPackageDuration } from "@/utils/convert-package-duration";
import { useBookingFlow } from "../../context/use-booking-flow";
import { BookingFlowHeader } from "../booking-flow-header";
import { formatInTimeZone } from "date-fns-tz";

export const BookingCreatePage = () => {
  const [disableStartDate, setDisableStartDate] = useState(true);
  const [disableMonths, setDisableMonths] = useState(true);
  const [disableCredits, setDisableCredits] = useState(true);
  const [disableTake, setDisableTake] = useState(true);
  const [disableMembers, setDisableMembers] = useState(true);
  const [privateRoomCapacity, setPrivateRoomCapacity] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState(0);
  const [selectedPackageId, setSelectedPackageId] = useState(0);
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedRoomMonthlyPrice, setSelectedRoomMonthlyPrice] = useState(0);
  const [selectedPackageEndDate, setSelectedPackageEndDate] = useState("");

  const navigate = useNavigate();
  const {
    handSelectedCompany,
    handSelectedPackage,
    handSelectedSpace,
    handSelectedPrivateOffice,
    filterCompanies,
    filterPackages,
    filterSpaces,
    filterPrivateOffices,
    privateOfficeData,
  } = useFilters();

  const {
    getActiveCycle,
    creditAmount,
    creditsConsumption,
    remainingCredits,
    activeCycle,
  } = useGetPackages();

  const {
    handleSubmitBookingData,
    bookingData,
    setSimulatedUpsell,
    setSelectedCycle,
    setSelectedPackage,
    selectedPackage,
    setSelectedPackageGroups,
    selectedPackageGroups,
    setSelectedCompany,
    selectedCompany,
  } = useBookingFlow();

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    resetField,
    watch,
    formState: { isValid },
  } = useForm<CreateBookingFormData>({
    resolver: yupResolver(CreateBookingSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      ...bookingData,
      flow_type: undefined,
    },
  });

  const enableSubmitButton = isValid && !!activeCycle;

  const isCycleMonthly =
    selectedPackage && selectedPackage.duration === "monthly";

  useEffect(() => {
    if (privateOfficeData && bookingData?.private_room) {
      const privateOfficeDetails = privateOfficeData.find(
        (privateOffice: any) =>
          privateOffice.id === bookingData.private_room.id,
      );

      if (privateOfficeDetails) {
        setPrivateRoomCapacity(privateOfficeDetails.capacity);
        setSelectedRoomMonthlyPrice(privateOfficeDetails.monthly_price);
      }
    }
  }, [privateOfficeData, bookingData?.private_room]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (bookingData) {
        setSelectedStartDate(bookingData.start_date);
        await handSelectedCompany(bookingData.company.name);
        await handSelectedPackage(bookingData.company.id);
        await handSelectedSpace(bookingData.coworking.name);
        await handSelectedPrivateOffice(`${bookingData.coworking.id}`);

        await selectOptionProp("company", bookingData.company);
        await selectOptionProp("package", bookingData.package);
        await selectOptionProp("coworking", bookingData.coworking);
        await selectOptionProp("private_room", bookingData.private_room);

        await getActiveCycle(
          bookingData.start_date,
          bookingData.package.id,
          bookingData.company.id,
        );

        setDisableStartDate(false);
        setDisableMonths(false);
        setDisableCredits(false);
        setDisableTake(false);
        setDisableMembers(false);
      }
    };

    loadInitialData();
  }, [bookingData]);

  const selectOptionProp = async (
    name: string,
    item: PackageDetails | { id: number; name: string },
  ) => {
    const nameKey = name as keyof CreateBookingDataParams;
    clearErrors();
    setValue(nameKey, item);

    if (name === "company") {
      setSelectedCompanyId(item.id);
      setSelectedCompany(item);
      if (item.id) {
        await handSelectedPackage(item.id);
      }
    }

    if (name === "package") {
      resetField("flow_type");
      setSimulatedUpsell(null);
      setSelectedPackage(item as PackageDetails);
      setSelectedPackageId(item.id);
      selectedStartDate &&
        getActiveCycle(selectedStartDate, item.id, selectedCompanyId);

      if ("groups" in item) {
        if (item.groups.length && selectedCompany?.id) {
          GetCompanyGroupsByIdList(item.groups, selectedCompany.id).then(
            (groups) => {
              setSelectedPackageGroups(groups);
            },
          );
        }
      }

      if ("versions" in item) {
        setSelectedPackageEndDate(
          item.versions[item.versions.length - 1].endDate,
        );
      }

      if ("duration" in item && item.duration != "monthly") {
        setValue("flow_type", "default", { shouldValidate: true });
      }
    }

    if (name === "coworking") {
      await handSelectedPrivateOffice(`${item.id}`);

      if (item.id) {
        const value = await getSpaceTakeValue(item.id);
        setValue("take", value);
      }
    }

    if (name === "private_room" && item.name !== "") {
      setDisableStartDate(false);
      setDisableMonths(false);
      setDisableCredits(false);
      setDisableTake(false);
      setDisableMembers(false);
      if (privateOfficeData) {
        const privateOfficeDetails = privateOfficeData.find(
          (privateOffice: any) => privateOffice?.id === item.id,
        );

        if (privateOfficeDetails) {
          setPrivateRoomCapacity(privateOfficeDetails?.capacity);
          setSelectedRoomMonthlyPrice(privateOfficeDetails.monthly_price);
        }
      }
    }
  };

  const pastBooking = (date: string) => {
    const today = new Date();
    const bookingDate = parseISO(date);

    return isBefore(bookingDate, startOfMonth(today));
  };

  const onSubmit = async (data: CreateBookingDataParams) => {
    if (Number(data?.members) > privateRoomCapacity) {
      setErrorMsg(
        "A quantidade de membros não pode exceder a capacidade da sala",
      );
      return;
    }

    if (pastBooking(data.start_date)) {
      setErrorMsg(
        "Não é possível criar bookings para datas anteriores ao mês vigente",
      );
      return;
    }

    setErrorMsg("");
    handleSubmitBookingData(data);
  };

  const getPrivateRoomCreditsValue = async (
    privateRoomMonthlyPrice: number,
    unitPrice: number,
  ) => {
    const credits = CalcCreditsToConsume(unitPrice, privateRoomMonthlyPrice);

    setValue("credits", `${credits}`);
  };

  useEffect(() => {
    if (
      selectedCompanyId &&
      selectedRoomMonthlyPrice &&
      selectedPackageId &&
      selectedStartDate &&
      activeCycle
    ) {
      getPrivateRoomCreditsValue(
        selectedRoomMonthlyPrice,
        activeCycle.credits.unitPrice,
      );
    }
  }, [
    selectedCompanyId,
    selectedRoomMonthlyPrice,
    selectedPackageId,
    selectedStartDate,
    activeCycle,
  ]);

  useEffect(() => {
    if (selectedStartDate && selectedPackageEndDate) {
      const parsedStartDate = parse(
        selectedStartDate,
        "yyyy-MM-dd",
        new Date(),
      );

      const parsedEndDate = parseISO(selectedPackageEndDate);

      const numberOfMonhts =
        differenceInCalendarMonths(parsedEndDate, parsedStartDate) + 1;

      setValue("months", `${numberOfMonhts}`);
    }
  }, [selectedStartDate, selectedPackageEndDate]);

  useEffect(() => {
    if (activeCycle) {
      setSelectedCycle(activeCycle);
    }
  }, [activeCycle]);

  return (
    <div className="bg-slate-50">
      <div className="flex flex-col justify-start items-start pb-4 max-w-4xl mx-auto">
        <BookingFlowHeader
          title="Criando reserva de escritório privativo"
          subtitle="Associe um escritório privativo à uma empresa contrante. Todos os
          campos são obrigatórios."
          onClick={() => navigate("/private-office/bookings")}
        />
        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex-col justify-items-start w-full gap-8 inline-flex flex mb-8">
            <div className="self-stretch flex-col justify-start items-start gap-8 flex mt-8">
              <div className="self-stretch flex-col justify-start items-start gap-4 flex">
                <div className="self-stretch justify-start items-start gap-4 inline-flex">
                  <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                    <div className="justify-start items-start inline-flex">
                      <div className="text-bg-black text-sm font-medium">
                        Empresa
                      </div>
                    </div>
                    <Controller
                      control={control}
                      name="company"
                      render={({ field, fieldState }) => (
                        <SearchInputCommand
                          {...field}
                          name="company"
                          chevron={true}
                          selectOption={selectOptionProp}
                          searchValue={field.value?.name as string}
                          optionsList={filterCompanies!}
                          updateList={handSelectedCompany}
                          label="Busque por nome ou ID"
                          defaultValue={bookingData?.company?.name}
                          onChange={(event) => {
                            handSelectedCompany(event.target.value);
                            field.onChange({ name: event.target.value });
                          }}
                          type="text"
                          value={field.value?.name || ""}
                          error={
                            fieldState.error ? fieldState.error.message : ""
                          }
                          inputWidth="w-full"
                        />
                      )}
                    />
                  </div>
                  <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                    <div className="justify-start items-start inline-flex">
                      <div className="text-bg-black text-sm font-medium">
                        Pacote
                      </div>
                    </div>
                    <Controller
                      control={control}
                      name="package"
                      render={({ field, fieldState }) => (
                        <SearchInputCommand
                          {...field}
                          name="package"
                          chevron={true}
                          selectOption={selectOptionProp}
                          searchValue={field.value?.name as string}
                          optionsList={filterPackages!}
                          disabled={!selectedCompanyId}
                          label="Busque por ID"
                          defaultValue={bookingData?.package?.name}
                          onChange={(event) => {
                            field.onChange({ name: event.target.value });
                          }}
                          searchDisable={true}
                          type="text"
                          value={field.value?.name || ""}
                          error={
                            fieldState.error ? fieldState.error.message : ""
                          }
                          inputWidth="w-full"
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="self-stretch justify-start items-start gap-4 inline-flex">
                  <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                    <div className="justify-start items-start inline-flex">
                      <div className="text-bg-black text-sm font-medium">
                        Espaço
                      </div>
                    </div>
                    <Controller
                      control={control}
                      name="coworking"
                      render={({ field, fieldState }) => (
                        <SearchInputCommand
                          {...field}
                          name="coworking"
                          chevron={true}
                          selectOption={selectOptionProp}
                          searchValue={field.value?.name as string}
                          optionsList={filterSpaces!}
                          updateList={handSelectedSpace}
                          label="Busque por nome ou ID"
                          defaultValue={bookingData?.coworking?.name}
                          onChange={(event) => {
                            handSelectedSpace(event.target.value);
                            field.onChange({ name: event.target.value });
                          }}
                          type="text"
                          value={field.value?.name || ""}
                          error={
                            fieldState.error ? fieldState.error.message : ""
                          }
                          inputWidth="w-full"
                        />
                      )}
                    />
                  </div>
                  <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                    <div className="justify-start items-start inline-flex">
                      <div className="text-bg-black text-sm font-medium">
                        Escritório Privativo
                      </div>
                    </div>
                    <Controller
                      control={control}
                      name="private_room"
                      render={({ field, fieldState }) => (
                        <SearchInputCommand
                          {...field}
                          name="private_room"
                          chevron={true}
                          selectOption={selectOptionProp}
                          searchValue={field.value?.name as string}
                          optionsList={filterPrivateOffices!}
                          updateList={handSelectedPrivateOffice}
                          label="Busque por nome ou ID"
                          defaultValue={bookingData?.private_room?.name}
                          onChange={(event) => {
                            handSelectedCompany(event.target.value);
                            field.onChange({ name: event.target.value });
                          }}
                          type="text"
                          value={field.value?.name || ""}
                          error={
                            fieldState.error ? fieldState.error.message : ""
                          }
                          inputWidth="w-full"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch flex-col justify-start items-start gap-2.5 flex">
              <div className="self-stretch h-px border border-[#e3e3e7]" />
            </div>
            <div className="self-stretch flex-col justify-start items-start gap-4 flex">
              <div className="self-stretch flex-col justify-start items-start gap-4 flex">
                <div className="self-stretch justify-start items-start gap-4 inline-flex">
                  <div className="self-stretch grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                    <div className="justify-start items-start inline-flex">
                      <div className="text-bg-black text-sm font-medium">
                        Data de início
                      </div>
                    </div>
                    <div className="w-full">
                      <Controller
                        control={control}
                        name="start_date"
                        render={({ field, fieldState }) => (
                          <Input
                            {...field}
                            label="Start date"
                            name="start_date"
                            type="date"
                            value={field.value || ""}
                            onChange={(event) => {
                              field.onChange(event);
                              setSelectedStartDate(event.target.value);
                              getActiveCycle(
                                event.target.value,
                                selectedPackageId,
                                selectedCompanyId,
                              );
                            }}
                            disable={disableStartDate}
                            required
                            error={
                              (selectedStartDate &&
                                !activeCycle &&
                                "Não existe um ciclo na data informada para o pacote selecionado. Tente uma outra data.") ||
                              (fieldState.error ? fieldState.error.message : "")
                            }
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                    <div className="justify-start items-start inline-flex">
                      <div className="text-bg-black text-sm font-medium">
                        Duração (em meses)
                      </div>
                    </div>
                    <div className="w-full">
                      <Controller
                        control={control}
                        name="months"
                        render={({ field, fieldState }) => (
                          <Input
                            {...field}
                            label="Months"
                            name="months"
                            type="text"
                            value={field.value || ""}
                            onChange={(event) => {
                              field.onChange({
                                ...event,
                                target: {
                                  ...event.target,
                                  value: JustNumber(event.target.value),
                                },
                              });
                            }}
                            disable={disableMonths}
                            required
                            error={
                              fieldState.error ? fieldState.error.message : ""
                            }
                            placeholder="Digite a duração em meses"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className="self-stretch justify-start items-start gap-4 inline-flex">
                  <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                    <div className="justify-start items-start inline-flex">
                      <div className="text-bg-black text-sm font-medium">
                        Porcentagem de Take
                      </div>
                    </div>
                    <div className="w-full">
                      <Controller
                        control={control}
                        name="take"
                        render={({ field, fieldState }) => (
                          <Input
                            {...field}
                            label="Take %"
                            name="take"
                            type="text"
                            value={field.value}
                            onChange={(event) => {
                              field.onChange({
                                ...event,
                                target: {
                                  ...event.target,
                                  value: JustNumber(event.target.value),
                                },
                              });
                            }}
                            disable={disableTake}
                            required
                            error={
                              fieldState.error ? fieldState.error.message : ""
                            }
                            placeholder="Digite a porcentagem"
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                    <div className="justify-start items-start inline-flex">
                      <div className="text-bg-black text-sm font-medium">
                        Créditos mensais
                      </div>
                    </div>
                    <div className="w-full">
                      <Controller
                        control={control}
                        name="credits"
                        render={({ field, fieldState }) => (
                          <Input
                            {...field}
                            label="Créditos"
                            name="credits"
                            type="text"
                            inputMode="decimal"
                            value={field.value}
                            onChange={(event) => {
                              field.onChange({
                                ...event,
                                target: {
                                  ...event.target,
                                  value: JustDecimal(event.target.value),
                                },
                              });
                            }}
                            disable={disableCredits}
                            required
                            error={
                              fieldState.error ? fieldState.error.message : ""
                            }
                            placeholder="Digite o número de créditos mensais"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className="self-stretch justify-start items-start inline-flex">
                  <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                    <div className="justify-start items-start inline-flex">
                      <div className="text-bg-black text-sm font-medium">
                        Membros
                      </div>
                    </div>
                    <div className="w-full">
                      <Controller
                        control={control}
                        name="members"
                        render={({ field, fieldState }) => (
                          <Input
                            {...field}
                            label="Members"
                            name="members"
                            type="text"
                            value={field.value || ""}
                            onChange={(event) => {
                              field.onChange({
                                ...event,
                                target: {
                                  ...event.target,
                                  value: JustNumber(event.target.value),
                                },
                              });
                            }}
                            disable={disableMembers}
                            required
                            error={
                              fieldState.error ? fieldState.error.message : ""
                            }
                            placeholder="Digite a quantidade de membros"
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="relative" />
                </div>
              </div>
            </div>
            <div className="self-stretch flex-col justify-start items-start gap-2.5 flex">
              <div className="self-stretch border border-[#e3e3e7]" />
            </div>

            {selectedPackageId == 0 ? (
              <div className="self-stretch bg-white rounded-lg border border-[#e3e3e7] flex-col justify-start items-start flex">
                <div className="self-stretch p-6 justify-start items-start inline-flex">
                  <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex gap-1">
                    <div className="self-stretch text-bg-black text-2xl font-semibold">
                      Pacote e Ciclo
                    </div>
                    <div className="self-stretch h-5 text-bg-black text-sm font-normal leading-tight">
                      Selecione um pacote para ver mais informações.
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="self-stretch bg-white rounded-md shadow border border-[#e3e3e7] flex-col justify-start items-start flex">
                <div className="self-stretch flex-col justify-start items-start flex">
                  <div className="self-stretch px-6 pt-6 justify-start items-start gap-2 inline-flex">
                    <div className="grow shrink basis-0 flex-col justify-center items-start gap-1.5 inline-flex">
                      <div className="text-bg-black text-2xl font-semibold leading-loose">
                        Pacote
                      </div>
                    </div>
                  </div>
                  <div className="self-stretch p-6 flex-col justify-start items-start gap-4 flex">
                    <div className="self-stretch justify-start items-start gap-4 inline-flex">
                      <div className="grow shrink basis-0 self-stretch justify-start items-center gap-4 flex">
                        <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
                          <div className="self-stretch text-bg-black text-sm font-semibold">
                            {selectedPackageId && <p>{selectedPackageId}</p>}
                          </div>
                          <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                            ID do pacote
                          </div>
                        </div>
                      </div>
                      <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
                        <div className="self-stretch text-bg-black text-sm font-semibold">
                          {selectedPackage && (
                            <p>
                              {selectedPackage.products
                                .map((product) => {
                                  return ConvertProductToName(product);
                                })
                                .join(", ")}
                            </p>
                          )}
                        </div>
                        <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                          Produtos
                        </div>
                      </div>
                      <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
                        <div className="self-stretch text-bg-black text-sm font-semibold">
                          {selectedPackageGroups && (
                            <p>
                              {selectedPackageGroups.length
                                ? selectedPackageGroups
                                    .map((group) => group.name)
                                    .join(", ")
                                : "Company wide"}
                            </p>
                          )}
                        </div>
                        <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                          Grupos
                        </div>
                      </div>
                    </div>
                    <div className="self-stretch justify-start items-start gap-4 inline-flex">
                      <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
                        <div className="self-stretch text-bg-black text-sm font-semibold">
                          {selectedPackage && (
                            <p>
                              {formatInTimeZone(
                                selectedPackage.versions[0].startDate,
                                "UTC",
                                "dd/MM/yyyy",
                              )}
                            </p>
                          )}
                        </div>
                        <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                          Data de início
                        </div>
                      </div>
                      <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
                        <div className="self-stretch text-bg-black text-sm font-semibold">
                          {selectedPackage && (
                            <p>
                              {formatInTimeZone(
                                selectedPackage.versions[
                                  selectedPackage.versions.length - 1
                                ].endDate,
                                "UTC",
                                "dd/MM/yyyy",
                              )}
                            </p>
                          )}
                        </div>
                        <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                          Data de fim
                        </div>
                      </div>
                      <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
                        <div className="self-stretch text-bg-black text-sm font-semibold">
                          {selectedPackage && (
                            <p>
                              {ConvertPackageDuration(selectedPackage.duration)}
                            </p>
                          )}
                        </div>
                        <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                          Duração do ciclo
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {selectedStartDate && activeCycle ? (
                  <div className="self-stretch flex-col justify-start items-start flex">
                    <div className="self-stretch px-6 justify-start items-start gap-2 inline-flex">
                      <div className="grow shrink basis-0 flex-col justify-center items-start gap-1.5 inline-flex">
                        <div className="text-bg-black text-2xl font-semibold leading-loose">
                          Ciclo
                        </div>
                        <div className="text-zinc-500 text-sm font-normal leading-tight">
                          Referente ao ciclo correspondente com a data de início
                          desta reserva.
                        </div>
                      </div>
                    </div>
                    <div className="self-stretch p-6 flex-col justify-start items-start gap-4 flex">
                      <div className="self-stretch justify-start items-start gap-4 inline-flex">
                        <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
                          <div className="self-stretch text-bg-black text-sm font-semibold">
                            {activeCycle && <p>{activeCycle.id}</p>}
                          </div>
                          <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                            ID do ciclo
                          </div>
                        </div>
                        <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
                          <div className="self-stretch text-bg-black text-sm font-semibold">
                            {activeCycle && (
                              <p>
                                {formatInTimeZone(
                                  activeCycle.startDate,
                                  "UTC",
                                  "dd/MM/yyyy",
                                )}
                              </p>
                            )}
                          </div>
                          <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                            Data de início
                          </div>
                        </div>
                        <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
                          <div className="self-stretch text-bg-black text-sm font-semibold">
                            {activeCycle && (
                              <p>
                                {formatInTimeZone(
                                  activeCycle.endDate,
                                  "UTC",
                                  "dd/MM/yyyy",
                                )}
                              </p>
                            )}
                          </div>
                          <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                            Data de fim
                          </div>
                        </div>
                      </div>
                      <div className="self-stretch justify-start items-start gap-4 inline-flex">
                        <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
                          <div className="self-stretch text-bg-black text-sm font-semibold">
                            {activeCycle && <p>{creditAmount} créditos</p>}
                          </div>
                          <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                            Quantidade de créditos
                          </div>
                        </div>
                        <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
                          <div className="self-stretch text-bg-black text-sm font-semibold">
                            {activeCycle && (
                              <p>{creditsConsumption} créditos</p>
                            )}
                          </div>
                          <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                            Utilizados por escritório privativo
                          </div>
                        </div>
                        <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
                          <div className="self-stretch text-bg-black text-sm font-semibold">
                            {activeCycle && (
                              <p>{Math.max(0, remainingCredits)} créditos</p>
                            )}
                          </div>
                          <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                            Créditos disponíveis
                          </div>
                        </div>
                      </div>
                      <div className="self-stretch justify-start items-start gap-4 inline-flex">
                        <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
                          <div className="self-stretch text-bg-black text-sm font-semibold">
                            {activeCycle && (
                              <p>
                                R$ {activeCycle.credits.unitPrice.toFixed(2)}
                              </p>
                            )}
                          </div>
                          <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                            Valor unitário do crédito
                          </div>
                        </div>
                        <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
                          <div className="self-stretch text-bg-black text-sm font-semibold">
                            {activeCycle && (
                              <p>
                                R${" "}
                                {activeCycle.credits.exceededPrice.toFixed(2)}
                              </p>
                            )}
                          </div>
                          <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                            Valor do crédito excedente
                          </div>
                        </div>
                        <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1 inline-flex">
                          <div className="self-stretch text-bg-black text-sm font-semibold">
                            {activeCycle && (
                              <p>{activeCycle.credits.courtesy} créditos</p>
                            )}
                          </div>
                          <div className="self-stretch text-zinc-500 text-sm font-normal leading-tight">
                            Cortesias
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="self-stretch p-6 justify-start items-start inline-flex">
                    <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex gap-1">
                      <div className="self-stretch text-bg-black text-2xl font-semibold">
                        Ciclo
                      </div>
                      <div className="self-stretch h-5 text-bg-black text-sm font-normal leading-tight">
                        Escolha uma data de início para mostrar o ciclo
                        correspondente.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isProRataDate(selectedStartDate) && (
              <div className="flex-col justify-start items-start gap-1 self-stretch flex">
                <Alert className="bg-amber-50 rounded-lg border border-yellow-500/50 w-full flex-col justify-start items-start gap-1 inline-flex">
                  <div className="flex gap-3  items-center pl-6">
                    <div className="absolute top-4 left-4">
                      <SirenIcon
                        className="text-yellow-900"
                        width={16}
                        height={16}
                      />
                    </div>
                    <div>
                      <AlertTitle className="text-base font-medium text-yellow-900 mb-0">
                        Atenção: Pró-rata será aplicada!
                      </AlertTitle>
                      <AlertDescription className="self-stretch text-yellow-900 text-sm font-normal leading-tight">
                        Este booking possui uma data de início que não coincide
                        com o começo do mês.
                      </AlertDescription>
                      <h6 className="self-stretch text-yellow-900 text-sm font-normal leading-tight">
                        Os valores de repasse e créditos serão calculados
                        proporcionalmente aos dias restantes do mês atual.
                      </h6>
                    </div>
                  </div>
                </Alert>
              </div>
            )}

            <div className="flex-col justify-start items-start gap-1 self-stretch flex">
              <Alert className="bg-white rounded-lg border border-[#e3e3e7] flex-col justify-start items-start gap-1 inline-flex p-4">
                <div className="flex gap-3  items-center pl-6">
                  <div className="absolute top-5 left-4">
                    <Receipt width={16} height={16} />
                  </div>
                  <div>
                    <AlertTitle className="text-base font-medium text-bg-black mb-0">
                      Escolha de que forma será feita esta cobrança.
                    </AlertTitle>
                    <AlertDescription className="self-stretch text-bg-black text-sm font-normal leading-tight">
                      Você pode realizar esta reserva sem editar os ciclos do
                      pacote, ou optar por ajustá-los para realizar um upsell.
                    </AlertDescription>
                    <div className="flex-col justify-start place-items-baseline inline-flex mt-2">
                      <div className="justify-start items-center gap-2 inline-flex max-h-[25px]">
                        <Controller
                          control={control}
                          name="flow_type"
                          render={({ field }) => (
                            <input
                              {...field}
                              type="radio"
                              id="flow_type_default"
                              value="default"
                              className="h-4 w-4"
                              onChange={(e) => {
                                field.onChange(e);
                              }}
                              checked={field.value === "default"}
                            />
                          )}
                        />
                        <label
                          htmlFor="flow_type_default"
                          className="text-bg-black text-sm font-medium"
                        >
                          Criar reserva sem editar ciclos.
                        </label>
                      </div>
                      <div className="justify-start items-center gap-2 inline-flex max-h-[25px]">
                        <Controller
                          control={control}
                          name="flow_type"
                          render={({ field }) => (
                            <input
                              {...field}
                              type="radio"
                              value="upsell"
                              id="flow_type_upsell"
                              className="h-4 w-4 peer disabled:cursor-not-allowed"
                              onChange={(e) => {
                                field.onChange(e);
                              }}
                              disabled={!isCycleMonthly}
                              checked={field.value === "upsell"}
                            />
                          )}
                        />
                        <label
                          htmlFor="flow_type_upsell"
                          className="text-black text-sm font-medium peer-disabled:text-gray-400 peer-disabled:cursor-not-allowed"
                        >
                          Editar ciclos e ajustar créditos.
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </Alert>
            </div>
          </div>

          {errorMsg !== "" && (
            <div className="p-2 bg-red-100 text-red-600 rounded border border-red-200 m-0">
              {errorMsg}
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-x-6">
            <button
              type="button"
              className="text-sm font-semibold leading-6 text-zinc-950"
              onClick={() => navigate(-1)}
            >
              Cancelar
            </button>
            <Button type="submit" disabled={!enableSubmitButton}>
              {watch("flow_type") === "upsell"
                ? "Avançar para pagamentos"
                : "Avançar para Revisão"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
